import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { X, Check } from "lucide-react";

/* ----------------------------- HELPERS ----------------------------- */
const peso = (s) => Number(String(s).replace(/[₱,\s]/g, ""));
const fmtPeso = (n) => "₱" + n.toLocaleString("en-PH");

/* ------------------------------ DATA ------------------------------ */
/* Source: SOLARES official price list (Summer Promo, ₱20K–50K discount).
   Panels: Bi-Facial 620W. Inverter: DEYE / SOLIS. Prices VAT-exclusive. */

const GRID_TIE = [
  {
    kw: "3.72 kW",
    tag: "Grid Tie",
    srp: "₱200,000",
    price: "₱180,000",
    panels: "6× Bi-Facial 620W",
    inverter: "3.6 kW",
    inclusions: [
      "6 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 3.6 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "5.58 kW",
    tag: "Grid Tie",
    srp: "₱240,000",
    price: "₱220,000",
    popular: true,
    panels: "9× Bi-Facial 620W",
    inverter: "5 kW",
    inclusions: [
      "9 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 5 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "6.82 kW",
    tag: "Grid Tie",
    srp: "₱258,000",
    price: "₱238,000",
    panels: "11× Bi-Facial 620W",
    inverter: "6 kW",
    inclusions: [
      "11 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 6 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "8.68 kW",
    tag: "Grid Tie",
    srp: "₱309,000",
    price: "₱289,000",
    panels: "14× Bi-Facial 620W",
    inverter: "8 kW",
    inclusions: [
      "14 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 8 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "10.54 kW",
    tag: "Grid Tie",
    srp: "₱350,000",
    price: "₱330,000",
    panels: "17× Bi-Facial 620W",
    inverter: "10 kW",
    inclusions: [
      "17 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 10 kW",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
];

const HYBRID_100 = [
  {
    kw: "3.72 kW",
    tag: "Hybrid",
    battery: "100Ah",
    srp: "₱280,000",
    price: "₱260,000",
    panels: "6× Bi-Facial 620W",
    inverter: "3.6 kW",
    inclusions: [
      "6 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 3.6 kW",
      "100Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "5.58 kW",
    tag: "Hybrid",
    battery: "100Ah",
    srp: "₱305,000",
    price: "₱285,000",
    panels: "9× Bi-Facial 620W",
    inverter: "5 kW",
    inclusions: [
      "9 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 5 kW",
      "100Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "6.82 kW",
    tag: "Hybrid",
    battery: "100Ah",
    srp: "₱333,000",
    price: "₱313,000",
    panels: "11× Bi-Facial 620W",
    inverter: "6 kW",
    inclusions: [
      "11 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 6 kW",
      "100Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "8.68 kW",
    tag: "Hybrid",
    battery: "100Ah",
    srp: "₱383,000",
    price: "₱363,000",
    panels: "14× Bi-Facial 620W",
    inverter: "8 kW",
    inclusions: [
      "14 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 8 kW",
      "100Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "10.54 kW",
    tag: "Hybrid",
    battery: "100Ah",
    srp: "₱442,000",
    price: "₱422,000",
    panels: "17× Bi-Facial 620W",
    inverter: "10 kW",
    inclusions: [
      "17 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 10 kW",
      "100Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "12.4 kW",
    tag: "Hybrid",
    battery: "100Ah",
    srp: "₱495,000",
    price: "₱470,000",
    panels: "20× Bi-Facial 620W",
    inverter: "12 kW",
    inclusions: [
      "20 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 12 kW",
      "100Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
];

const HYBRID_200 = [
  {
    kw: "5.58 kW",
    tag: "Hybrid",
    battery: "200Ah",
    srp: "₱355,000",
    price: "₱335,000",
    panels: "9× Bi-Facial 620W",
    inverter: "5 kW",
    inclusions: [
      "9 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 5 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "6.82 kW",
    tag: "Hybrid",
    battery: "200Ah",
    srp: "₱380,000",
    price: "₱360,000",
    panels: "11× Bi-Facial 620W",
    inverter: "6 kW",
    inclusions: [
      "11 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 6 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "8.68 kW",
    tag: "Hybrid",
    battery: "200Ah",
    srp: "₱430,000",
    price: "₱410,000",
    panels: "14× Bi-Facial 620W",
    inverter: "8 kW",
    inclusions: [
      "14 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 8 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "10.54 kW",
    tag: "Hybrid",
    battery: "200Ah",
    srp: "₱495,000",
    price: "₱470,000",
    panels: "17× Bi-Facial 620W",
    inverter: "10 kW",
    inclusions: [
      "17 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 10 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "12.4 kW",
    tag: "Hybrid",
    battery: "200Ah",
    srp: "₱541,000",
    price: "₱516,000",
    panels: "20× Bi-Facial 620W",
    inverter: "12 kW",
    inclusions: [
      "20 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 12 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "16.7 kW",
    tag: "Hybrid",
    battery: "200Ah",
    srp: "₱647,000",
    price: "₱607,000",
    panels: "27× Bi-Facial 620W",
    inverter: "16 kW",
    inclusions: [
      "27 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 16 kW",
      "200Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
];

const HYBRID_314 = [
  {
    kw: "6.82 kW",
    tag: "Hybrid",
    battery: "314Ah",
    srp: "₱405,000",
    price: "₱385,000",
    panels: "11× Bi-Facial 620W",
    inverter: "6 kW",
    inclusions: [
      "11 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 6 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "8.68 kW",
    tag: "Hybrid",
    battery: "314Ah",
    srp: "₱458,000",
    price: "₱438,000",
    panels: "14× Bi-Facial 620W",
    inverter: "8 kW",
    inclusions: [
      "14 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 8 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "10.54 kW",
    tag: "Hybrid",
    battery: "314Ah",
    srp: "₱510,000",
    price: "₱490,000",
    panels: "17× Bi-Facial 620W",
    inverter: "10 kW",
    inclusions: [
      "17 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 10 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "12.4 kW",
    tag: "Hybrid",
    battery: "314Ah",
    srp: "₱564,000",
    price: "₱534,000",
    panels: "20× Bi-Facial 620W",
    inverter: "12 kW",
    inclusions: [
      "20 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 12 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "16.7 kW",
    tag: "Hybrid",
    battery: "314Ah",
    srp: "₱680,000",
    price: "₱630,000",
    panels: "27× Bi-Facial 620W",
    inverter: "16 kW",
    inclusions: [
      "27 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 16 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
  {
    kw: "18.6 kW",
    tag: "Hybrid",
    battery: "314Ah",
    srp: "₱774,000",
    price: "₱724,000",
    panels: "30× Bi-Facial 620W",
    inverter: "18 kW",
    inclusions: [
      "30 pcs Bi-Facial 620W Solar Panel",
      "DEYE / SOLIS Inverter 18 kW",
      "314Ah LiFePO₄ Battery 51.2V",
      "Aluminum racking & mounting hardware",
      "DC/AC cable, conduit & accessories",
    ],
  },
];

/* ----------------------------- PRODUCT MODAL ----------------------------- */
function ProductModal({ open, onClose, pkg }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !pkg) return null;
  const isHybrid = pkg.tag?.toLowerCase().includes("hybrid");
  const save = pkg.srp ? peso(pkg.srp) - peso(pkg.price) : 0;

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center font-inter"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-[min(92vw,900px)] max-h-[90svh] rounded-2xl bg-card text-text-primary overflow-hidden flex flex-col ring-1 ring-border-light shadow-2xl"
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 24 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full bg-text-primary/5 hover:bg-text-primary/10 flex items-center justify-center transition"
        >
          <X size={20} />
        </button>

        <div className="overflow-y-auto flex-1 space-y-8 p-8">
          {/* Header */}
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-start">
              <span className="rounded-lg bg-accent-sun/15 px-4 py-2 text-sm font-bold text-accent-sun border border-accent-sun/40">
                {pkg.tag}
              </span>
              {pkg.battery && (
                <span className="rounded-lg bg-accent-green/15 px-4 py-2 text-sm font-bold text-accent-green border border-accent-green/40">
                  🔋 {pkg.battery} Battery
                </span>
              )}
            </div>
            <h2 className="font-sharetech text-4xl md:text-5xl font-bold text-text-primary">{pkg.kw}</h2>

            {/* Pricing */}
            <div className="flex items-baseline gap-3 flex-wrap">
              <p className="font-sharetech text-3xl text-accent-sun font-bold">{pkg.price}</p>
              {pkg.srp && (
                <p className="text-xl text-text-muted line-through">{pkg.srp}</p>
              )}
            </div>
            {save > 0 && (
              <p className="inline-flex items-center gap-2 rounded-full bg-accent-green/10 border border-accent-green/30 px-3 py-1 text-sm font-semibold text-accent-green">
                ☀️ Summer Promo — save {fmtPeso(save)}
              </p>
            )}
            <p className="text-xs text-text-muted">
              Prices are VAT-exclusive and may change without prior notice.
            </p>
          </div>

          {/* Inclusions */}
          <section className="space-y-4">
            <h3 className="font-bold text-lg font-sharetech text-text-primary">What's Included</h3>
            <ul className="space-y-3">
              {pkg.inclusions.map((item) => (
                <li key={item} className="flex gap-3 items-start">
                  <Check size={20} className="text-accent-green flex-shrink-0 mt-0.5" />
                  <span className="text-text-body">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Warranties */}
          <section className="space-y-4">
            <h3 className="font-bold text-lg font-sharetech text-text-primary">Warranties</h3>
            <ul className="space-y-2 text-text-body">
              <li className="flex gap-3 items-start">
                <Check size={18} className="text-accent-sun flex-shrink-0 mt-0.5" />
                <span>Bi-Facial Solar Panels (12 Years)</span>
              </li>
              <li className="flex gap-3 items-start">
                <Check size={18} className="text-accent-sun flex-shrink-0 mt-0.5" />
                <span>DEYE / SOLIS Inverter (5 Years)</span>
              </li>
              {isHybrid && (
                <li className="flex gap-3 items-start">
                  <Check size={18} className="text-accent-sun flex-shrink-0 mt-0.5" />
                  <span>LiFePO₄ Battery (5 Years)</span>
                </li>
              )}
              <li className="flex gap-3 items-start">
                <Check size={18} className="text-accent-sun flex-shrink-0 mt-0.5" />
                <span>Workmanship (1 Year)</span>
              </li>
            </ul>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-border-light p-6 space-y-3">
          <div className="text-sm text-text-muted text-center">
            ✓ Transparent proposal • Clean cabling • Full documentation
          </div>
          <div className="flex gap-3 flex-col sm:flex-row">
            <a
              href="#contacts"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-lg bg-card border border-border-light text-text-body font-bold text-center transition hover:border-accent-sun hover:text-accent-sun"
            >
              Contact Us
            </a>
            <a
              href="#contacts"
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-lg bg-accent-sun hover:bg-orange-600 text-white font-bold text-center transition"
            >
              Request Quote
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

/* --------------------------------- PAGE --------------------------------- */
export default function Products() {
  const CATEGORIES = useMemo(
    () => [
      { id: "grid", label: "Grid-Tie", data: GRID_TIE },
      { id: "h100", label: "Hybrid 100Ah", data: HYBRID_100 },
      { id: "h200", label: "Hybrid 200Ah", data: HYBRID_200 },
      { id: "h314", label: "Hybrid 314Ah", data: HYBRID_314 },
    ],
    []
  );

  const [activeTab, setActiveTab] = useState(0);
  const [activePkg, setActivePkg] = useState(null);
  const [productOpen, setProductOpen] = useState(false);

  const currentData = CATEGORIES[activeTab].data;

  const openProduct = (pkg) => {
    setActivePkg(pkg);
    setProductOpen(true);
  };

  return (
    <section id="products" className="relative w-full bg-base text-text-primary py-20 md:py-28 font-sora">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-base" />
      </div>

      {/* Content */}
      <div className="max-w-[92rem] mx-auto px-5 sm:px-8 md:px-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-sun/10 border border-accent-sun/30 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-accent-sun mb-4">
            ☀️ Summer Promo • ₱20K–50K Off
          </span>
          <h2 className="font-sharetech text-4xl md:text-5xl font-bold mb-2 text-text-primary">Solar Packages</h2>
          <div className="h-1 w-32 bg-accent-sun/40 rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-body max-w-2xl mx-auto">
            Premium solar systems engineered for Filipino homes. Choose grid-tie or hybrid with battery backup.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          viewport={{ once: true }}
        >
          {CATEGORIES.map((cat, idx) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(idx)}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeTab === idx
                  ? "bg-gradient-to-br from-amber-400 via-accent-sun to-orange-600 text-white shadow-md shadow-orange-900/20 ring-1 ring-white/30"
                  : "bg-card text-text-body border border-border-light hover:-translate-y-0.5 hover:border-accent-sun hover:text-accent-sun hover:shadow-sm"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>

        {/* Products Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <AnimatePresence mode="wait">
            {currentData.map((pkg, idx) => {
              const save = pkg.srp ? peso(pkg.srp) - peso(pkg.price) : 0;
              return (
                <motion.button
                  key={`${pkg.kw}-${pkg.battery || "no-battery"}`}
                  onClick={() => openProduct(pkg)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: idx * 0.05, duration: 0.4 }}
                  className="group relative h-full text-left"
                >
                  <div
                    className={`h-full rounded-xl border-2 transition duration-300 p-6 space-y-4 ${
                      pkg.popular
                        ? "border-accent-sun bg-accent-sun/5 shadow-md"
                        : "border-border-light bg-card hover:border-accent-sun/50 hover:shadow-md"
                    }`}
                  >
                    {/* Popular badge */}
                    {pkg.popular && (
                      <div className="absolute top-3 right-3 rounded-full bg-accent-sun text-white px-3 py-1 text-xs font-bold uppercase">
                        Most Popular
                      </div>
                    )}

                    {/* System size */}
                    <div>
                      <div className="font-sharetech text-3xl font-bold text-text-primary mb-1">{pkg.kw}</div>
                      <div className="text-sm text-text-muted uppercase tracking-wide">
                        {pkg.tag} {pkg.battery && `• ${pkg.battery}`}
                      </div>
                    </div>

                    {/* Price */}
                    <div className="py-4 border-t border-border-light">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-sharetech text-2xl font-bold text-accent-sun">{pkg.price}</span>
                        {pkg.srp && (
                          <span className="text-sm text-text-muted line-through">{pkg.srp}</span>
                        )}
                      </div>
                      {save > 0 && (
                        <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-accent-green">
                          Save {fmtPeso(save)}
                        </div>
                      )}
                    </div>

                    {/* Quick specs */}
                    <div className="space-y-2 text-sm text-text-muted">
                      <div>✓ {pkg.panels}</div>
                      <div>✓ {pkg.inverter} Inverter</div>
                      {pkg.battery && <div>✓ {pkg.battery} LiFePO₄ Battery</div>}
                    </div>

                    {/* CTA */}
                    <div className="pt-4 mt-4 border-t border-border-light">
                      <span className="inline-flex items-center gap-1 text-accent-sun font-bold text-sm group-hover:gap-2 transition">
                        View Details <span>→</span>
                      </span>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {/* Fine print */}
        <p className="mt-8 text-center text-sm text-text-muted">
          Prices are VAT-exclusive and may change without prior notice. Includes 12-yr panel, 5-yr inverter,
          5-yr battery &amp; 1-yr workmanship warranty.
        </p>

        {/* Compare CTA */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="text-text-body mb-4">
            Need help choosing? Use our solar calculator to get a personalized recommendation.
          </p>
          <a
            href="/#calculator"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-text-primary hover:bg-slate-800 text-white font-bold transition transform hover:-translate-y-0.5 hover:shadow-md"
          >
            <span>Calculate Your Savings</span>
            <span>→</span>
          </a>
        </motion.div>
      </div>

      <ProductModal
        open={productOpen}
        onClose={() => setProductOpen(false)}
        pkg={activePkg}
      />
    </section>
  );
}
